/**
 * Build a per-turn citation-rules block that only enumerates the
 * retrieval source labels that actually contributed to this turn.
 *
 * Empty input → empty string. Callers use that signal to skip the
 * citation block entirely in the system prompt.
 */

export type RetrievalSource = 'm365' | 'aisearch' | 'web';

const ORDERED: readonly RetrievalSource[] = ['aisearch', 'm365', 'web'];

const LABEL: Record<RetrievalSource, string> = {
  aisearch: 'Azure AI Search results (`[Doc N]`)',
  m365: 'Microsoft 365 documents (`[M365 N]`)',
  web: 'web search results (`[Web N]`)',
};

function joinHuman(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

export function buildCitationInstructions(
  sources: RetrievalSource[]
): string {
  if (sources.length === 0) return '';
  const ordered = ORDERED.filter((s) => sources.includes(s));
  const enumeration = joinHuman(ordered.map((s) => LABEL[s]));
  return `Reference material includes ${enumeration}.

How to use it:
- Weave information from these sources into a single, coherent answer. Do not narrate the retrieval ("based on the web search...", "the documents say...") — just answer the question directly.
- Cite inline using the exact label shown above immediately after the specific fact it supports. When multiple sources back the same point, list each label one after another.
- Use markdown structure (short headings, bullet lists) when the answer has multiple parts; keep short answers conversational.
- Only cite when stating a specific fact from a source. Do not invent citations and do not cite for general knowledge.
- If the references don't cover the question, say so plainly and answer from general knowledge if appropriate.`;
}
